import { GrayRoundInput, GrayRoundTextArea, Text, Title, color } from "@smoothincorg/smooth-ui"
import { PhoneNumber, PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber"
import Link from "next/link"
import { useRouter } from "next/router"
import React, { useEffect, useMemo, useState } from "react"
import { BiDownArrow, BiUpArrow } from "react-icons/bi"
import { HiExternalLink } from "react-icons/hi"
import SlideDown from "react-slidedown"
import { StoreSelector } from "src/app/_components/StoreSelector"
import { A } from "src/components/A"
import { Border } from "src/components/Border"
import { FlexBox } from "src/components/Box"
import { BorderedButton } from "src/components/Button"
import { Checkbox } from "src/components/Checkbox"
import { List, formatDate } from "src/components/List/List"
import { Memo, MemoType } from "src/components/Memo"
import { Radios } from "src/components/Radios"
import { Select } from "src/components/Select"
import { Card } from "src/components/page-directory/Card"
import { useLineChatSWR } from "src/features/chat/hooks/useLineChatState"
import { MainExamination } from "src/features/lifftter/hooks/useMainExaminationSWR"
import { sortById } from "src/features/user/components/User/User"
import { useChatroom } from "src/hooks/useChatroom"
import { useMzdaoStatus } from "src/hooks/useMzdaoStatus"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { camelCase } from "src/lib/aikagi"
import { SMOOTH_SMOOTHER_API_URL, V1_OPE_URL } from "src/lib/constants/value"
import { dayjs } from "src/lib/dayjs"
import styled from "styled-components"
import useSWR from "swr"
import { AutoExaminationPart } from "./AutoExaminationPart"
import { ForCICProps } from "./ForCIC"
import { NoBypassUserSearchHouse } from "./NoBypassUserSearchHouse"
import { useNotification } from "@smoothincorg/smooth-ui"

export const CalcAge = (birthday: string) => {
  const today = dayjs()
  const birthDate = dayjs(birthday)
  const baseAge = today.year() - birthDate.year()
  const thisBirthday = dayjs(`${today.year()}-${birthDate.month() + 1}-${birthDate.date()}`)
  return today.isBefore(thisBirthday) ? baseAge - 1 : baseAge
}

export function FormatTelephoneNumber(value?: string) {
  if (!value) return ""
  try {
    const region = "JP"
    const util: PhoneNumberUtil = PhoneNumberUtil.getInstance()
    const number: PhoneNumber = util.parseAndKeepRawInput(value, region)
    if (!util.isValidNumberForRegion(number, region)) {
      return null
    }

    return util.format(number, PhoneNumberFormat.NATIONAL)
  } catch (e) {
    return value
  }
}

type ContainerProps = ForCICProps & {
  user: ForCICProps["users"][number]
  mes: ForCICProps["mainExaminationScores"][number]
  proposal: ForCICProps["proposals"][number]
  // searchHouse: ForCICProps['searchHouses'][number]
  mutateForCIC: (targetId: number) => void
}

type Props = ContainerProps & {
  id: number
  handleDestroyMemo: (memo: MemoType) => void
  storeSelectProps: {
    value: number
    onChange: (value: number) => void
  }
  handleOpenMemoForm: (memo: MemoType) => void
  memoFormState: MemoFormState
  handleChangeMemoForm: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  handleSubmitMemo: () => Promise<void>
  handleLendableLoanChange: React.ChangeEventHandler<HTMLSelectElement>
  shouldSendMessage: boolean
  handleShouldSendMessageChange: React.ChangeEventHandler<HTMLInputElement>
  availablePayment: number
  lendableLoan: number | "all"
  claimPaymentAmountOnAnnual: number
  handleSetClaimPaymentAmountOnAnnual: React.ChangeEventHandler<HTMLInputElement>
  handleResultButtonClick: React.MouseEventHandler<HTMLButtonElement>
  exception: boolean
  handleCheckException: React.ChangeEventHandler<HTMLInputElement>
  handleAdditionalAmountChange: React.ChangeEventHandler<HTMLInputElement>
  additionalAmountByException: number
  reasonForException: string
  handleReasonForExceptionChange: React.ChangeEventHandler<HTMLInputElement>
  reasonForDecision: string
  handleReasonForDecisionChange: React.ChangeEventHandler<HTMLInputElement>
}

const LENDABLE_LOANS = [
  [70000, "7万"],
  [100000, "10万"],
  [150000, "15万"],
  [200000, "20万"],
  [250000, "25万"],
  [300000, "30万"],
  [350000, "35万"],
  [400000, "40万"],
  [450000, "45万"],
  [500000, "50万"],
  [550000, "55万"],
  [600000, "60万"],
  [650000, "65万"],
  [700000, "70万"],
  [750000, "75万"],
  [800000, "80万"],
  [850000, "85万"],
  [900000, "90万"],
  [950000, "95万"],
  [1000000, "100万"],
  [1100000, "110万"],
  [1200000, "120万"],
  [1300000, "130万"],
  [1400000, "140万"],
  [1500000, "150万"],
  [1600000, "160万"],
  [1700000, "170万"],
  [1800000, "180万"],
  [1900000, "190万"],
  [2000000, "200万"],
] as const

// eslint-disable-next-line react/display-name
const Component = React.memo<Props>(({ user, mes, ...props }) => {
  const { showSnackbar } = useNotification()
  const { getWithToken, patchWithToken } = useSmoothApi()
  const router = useRouter()
  const isPending = !!router.query["pending"]
  const birthday = user.latest_simplified_examination?.birthday || user?.birthday
  const [openedScore, setOpenedScore] = useState<number[]>([])

  const [autoExam, setAutoExam] = useState(null)
  const [updateAutoExamKey, setUpdateAutoExamKey] = useState(0)

  const [displayPassActions, setDisplayPassActions] = useState(false)
  const [displayRejectActions, setDisplayRejectActions] = useState(false)
  type toggleOption = {
    isPass?: boolean
    isReject?: boolean
    all?: boolean
  }
  const toggleDisplayActions = (args: toggleOption) => {
    const { isPass, isReject, all } = args
    if (typeof all === "boolean") {
      setDisplayPassActions(all)
      setDisplayRejectActions(all)
      return
    }

    if (typeof isPass === "boolean") {
      setDisplayPassActions(isPass)
      setDisplayRejectActions(!isPass)
      return
    }

    if (typeof isReject === "boolean") {
      setDisplayPassActions(!isReject)
      setDisplayRejectActions(isReject)
      return
    }
  }

  useEffect(() => {
    getWithToken(SMOOTH_SMOOTHER_API_URL + `/auto_examinations/examinations/${mes.id}`).then(
      (res) => {
        setAutoExam(res.data)
      }
    )
  }, [updateAutoExamKey])

  const handleSetOpenedScore = (id: number) => {
    setOpenedScore((prevOpenedScore) => {
      if (prevOpenedScore.includes(id)) {
        return prevOpenedScore.filter((i) => i !== id)
      } else {
        return [...prevOpenedScore, id]
      }
    })
  }

  const isJukyaku = useMemo(() => {
    if ((props.proposal?.introduction_status & 32) > 0) {
      return true
    }

    return false
  }, [props.proposal?.introduction_status])

  const mzdaoStatus = useMzdaoStatus({
    userId: user.id,
  })

  const chatroom = useChatroom({ userId: user.id })

  useLineChatSWR(chatroom.data?.id)

  const everyExaminationRelatedToProposal = useSWR(
    "/main_examination_scores_related_to_proposal/" + mes.id,
    async () => {
      const allMaData = await getWithToken(
        `${SMOOTH_SMOOTHER_API_URL}/proposals/${props.proposal.id}/moving_applications`
      )

      const allMovingApplication = allMaData.data["data"] as { id: number }[]

      const allExaminationData = allMovingApplication.map(async (ma) => {
        const mainExaminationData = await getWithToken(
          `${SMOOTH_SMOOTHER_API_URL}/moving_applications/${ma.id}/main_examinations`
        )

        const mainExaminations = mainExaminationData.data["data"] as {
          id: number
        }[]

        const mainExaminationsWithScores = await Promise.all(
          mainExaminations.map(async (mainExamination) => {
            const mainExaminationScoreData = await getWithToken(
              `${SMOOTH_SMOOTHER_API_URL}/main_examinations/${mainExamination.id}/main_examination_score`
            )
            return {
              ...(mainExamination as MainExamination),
              score: mainExaminationScoreData.data["data"],
            }
          })
        )

        return mainExaminationsWithScores.map((d) => camelCase(d))
      })

      const all = await Promise.all(allExaminationData)

      return sortById(all?.flat()).filter((d) => d.score.id !== mes.id)
    }
  )

  const handleAssignSmootherClick = async (mes_id: number) => {
    const result: any = await patchWithToken(
      `${SMOOTH_SMOOTHER_API_URL}/main_examination_scores/` + mes_id + "/assign_smoother"
    )

    showSnackbar(`${result.data.assigned_smoother}に変更しました`, "success")
    props.mutateForCIC(mes_id)
  }

  return (
    <Card className={`main-examination-score ${props.className}`} data-id={props.id}>
      <FlexBox className={`main-examination-score__row`} alignItems="center">
        <Text size={16} weight="bold">
          審査担当者
        </Text>
        <Text size={12} style={{ marginLeft: 10 }}>
          {mes.assigned_smoother || "担当者なし"}
        </Text>
        <BorderedButton
          style={{ marginLeft: 10 }}
          onClick={() => {
            if (!confirm("担当者を自分に変更しますか？")) return

            handleAssignSmootherClick(mes.id)
          }}
        >
          審査担当者を自分に変更する
        </BorderedButton>
      </FlexBox>
      <Border />
      <FlexBox className={`main-examination-score__row`} alignItems="center">
        <Checkbox
          text="照会データに含める"
          value={user.id}
          checked={props.checkedUsers.includes(user.id)}
          onChange={props.handleCheckUser}
        />
        <Text size={12} style={{ marginLeft: 10 }}>
          {dayjs(mes.created_at).format("YYYY-MM-DD HH:mm")}
        </Text>
      </FlexBox>
      <Border />
      <FlexBox className={`main-examination-score__row`} alignItems="center">
        <Title as="h3" size={14} weight="bold">
          照会キー
        </Title>
        <FlexBox justifyContent="flex-start" alignItems="center">
          <a href={`${V1_OPE_URL}/admin/users/${user.id}`} target="_blank" rel="noreferrer">
            <Text size={14} color="main">
              {user.last_furigana}
              {` `}
              {user.first_furigana}
              <HiExternalLink />
            </Text>
          </a>
          <Link href={`/admin/users/${user.id}`}>
            <Text
              size={14}
              color="main"
              style={{
                textDecoration: "underline",
              }}
            >
              {user.id}
            </Text>
          </Link>

          <Text size={14}>
            {user.birthday_ja}({CalcAge(birthday)})
          </Text>
          <Text size={14}>{FormatTelephoneNumber(user.telephone_number)}</Text>
          <Text size={14}>{user.postal_code}</Text>
          <Text size={14}>{user.proposals_documents}</Text>
          <a href={`${user.around_initial_cost_url}`} target="_blank" rel="noreferrer">
            <Text size={14} color="main">
              {user.around_initial_cost}
              {user.around_initial_cost_status ? `(${user.around_initial_cost_status})` : null}
            </Text>
          </a>
          <Text size={14}>{user.latest_simplified_examination?.number_of_payment}回</Text>
        </FlexBox>
      </FlexBox>
      <Border />
      <FlexBox className={`main-examination-score__row`} alignItems="center">
        <Title as="h3" size={14} weight="bold">
          本人確認情報
        </Title>
        <FlexBox justifyContent="flex-start" alignItems="center">
          <span className="icon-src">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={user.icon_src} alt="user_icon" />
          </span>
          <Text size={14}>{user.line_user_name}</Text>
        </FlexBox>
      </FlexBox>
      <Border />
      <FlexBox className={`main-examination-score__row`} alignItems="center" flexWrap="wrap">
        <Title as="h3" size={14} weight="bold">
          ユーザー情報
        </Title>
        <FlexBox justifyContent="flex-start" alignItems="center">
          <Text size={14}>
            {user.latest_simplified_examination?.family_type || "家族形態未設定"}
          </Text>
          <Text size={14}>
            {user.latest_simplified_examination?.household + "人" || "世帯人数未設定"}
          </Text>
          <Text size={14}>
            {user.latest_simplified_examination?.income_yearly + "万円" || "年収未設定"}
          </Text>
          <Text size={14}>{user.latest_simplified_examination?.job}</Text>
          <Text size={14}>{user.latest_simplified_examination?.job_description}</Text>
          <a
            target="_blank"
            href={`https://www.google.com/search?q=${user.latest_simplified_examination?.company_name}`}
            rel="noreferrer"
          >
            <Text size={14} color="main">
              {user.latest_simplified_examination?.company_name}
            </Text>
          </a>
          {mzdaoStatus.data?.isMzdaoUser === true && <Text size={14}>{"KABU&ユーザー"}</Text>}
          {isJukyaku && <Text size={14}>※店舗利用ユーザー</Text>}
          <StoreSelector
            value={props.storeSelectProps.value}
            onChange={props.storeSelectProps.onChange}
          />
          <Link
            href={`${V1_OPE_URL}/admin/proposals/${props.proposal?.id}/edit_documents`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              color: color.main,
              textDecoration: "underline",
              fontSize: "1.4rem",
            }}
          >
            書類を確認する
          </Link>
          <Link
            href={`/admin/document/${props.proposal?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              color: color.main,
              textDecoration: "underline",
              fontSize: "1.4rem",
            }}
          >
            チャットで送付されたファイルを確認する
          </Link>
        </FlexBox>
      </FlexBox>
      <NoBypassUserSearchHouse proposalId={props.proposal.id} />

      <AutoExaminationPart
        autoExam={autoExam}
        mes_id={mes.id}
        autoExaminationLabel={mes.auto_examination_label}
        updateKey={updateAutoExamKey}
        setUpdateKey={setUpdateAutoExamKey}
        mesLabel={mes.auto_examination_label}
        interestAddOn={user.interest_add_on_when_examination}
      />

      <FlexBox
        className={`main-examination-score__row`}
        alignItems="center"
        justifyContent="flex-start"
      >
        <Title as="h3" size={14} weight="bold">
          支払可能見込み額
        </Title>
        <FlexBox justifyContent="flex-start" alignItems="center">
          <FlexBox justifyContent="flex-start" alignItems="center">
            <Text size={14}>年間請求予定額</Text>
            <GrayRoundInput
              value={props.claimPaymentAmountOnAnnual}
              onChange={props.handleSetClaimPaymentAmountOnAnnual}
            />
            <Text size={14}>千円</Text>
          </FlexBox>
        </FlexBox>
        <FlexBox justifyContent="flex-start" alignItems="center">
          <FlexBox
            justifyContent="flex-start"
            alignItems="center"
            className={`main-examination-score__actions`}
          >
            <A
              style={{
                backgroundColor: "#6FCF97",
              }}
              href={
                "/admin/chat?user_id=" +
                user.id +
                "&display_template_messages=false&call_local_storage=true"
              }
            >
              <Text color="white">チャット</Text>
            </A>
            <button
              style={{
                backgroundColor: "#F1D263",
              }}
              data-data={isPending ? "未完了" : "要確認"}
              data-main_examination_score_id={mes.id}
              onClick={props.handleResultButtonClick}
            >
              {isPending ? (
                <Text color="white">未完了に戻す</Text>
              ) : (
                <Text color="white">ヒアリング</Text>
              )}
            </button>
            <button
              style={{
                backgroundColor: color.main,
              }}
              onClick={() => {
                toggleDisplayActions({ isPass: true })
              }}
            >
              <Text color="white">通過</Text>
            </button>
            <button
              data-data="否認"
              data-main_examination_score_id={mes.id}
              onClick={() => {
                toggleDisplayActions({ isReject: true })
              }}
              style={{
                backgroundColor: color.red,
              }}
            >
              <Text color="white">否認</Text>
            </button>
          </FlexBox>
        </FlexBox>
      </FlexBox>
      <SlideDown>
        {displayRejectActions && (
          <Card className="pass-actions">
            <FlexBox alignItems="center">
              <Title as="h3" size={14} weight="bold">
                決裁理由
              </Title>
              <FlexBox>
                <GrayRoundInput
                  value={props.reasonForDecision}
                  onChange={props.handleReasonForDecisionChange}
                />
              </FlexBox>
            </FlexBox>
            <FlexBox justifyContent="center">
              <BorderedButton
                data-data="否認"
                data-main_examination_score_id={mes.id}
                style={{ marginRight: 8 }}
                onClick={(e) => {
                  props.handleResultButtonClick(e)
                }}
              >
                決定
              </BorderedButton>
              <BorderedButton
                onClick={() => {
                  console.log("clicked")
                  toggleDisplayActions({ all: false })
                }}
              >
                キャンセル
              </BorderedButton>
            </FlexBox>
          </Card>
        )}
        {displayPassActions && (
          <Card className="pass-actions">
            <FlexBox alignItems="center">
              <Title as="h3" size={14} weight="bold">
                支払可能見込額
              </Title>
              <FlexBox alignItems="center">
                <Text size={14}>{props.availablePayment / 10}万円</Text>
                <Checkbox
                  checked={props.exception}
                  onChange={props.handleCheckException}
                  text="特例を適用する"
                />
              </FlexBox>
            </FlexBox>
            {props.exception && (
              <FlexBox alignItems="center" className="adjust">
                <Title as="h3" size={14} weight="bold">
                  調整金額
                </Title>
                <FlexBox alignItems="center" justifyContent="flex-start">
                  <GrayRoundInput
                    value={props.additionalAmountByException}
                    onChange={props.handleAdditionalAmountChange}
                  />
                  <Text size={14}>千円</Text>
                </FlexBox>
              </FlexBox>
            )}
            {props.exception && (
              <FlexBox alignItems="center" className="adjust">
                <Title as="h3" size={14} weight="bold">
                  特例調査結果
                </Title>
                <FlexBox alignItems="center" justifyContent="flex-start">
                  <GrayRoundInput
                    value={props.reasonForException}
                    onChange={props.handleReasonForExceptionChange}
                  />
                </FlexBox>
              </FlexBox>
            )}
            <FlexBox alignItems="center">
              <Title as="h3" size={14} weight="bold">
                通知
              </Title>
              <Radios
                options={[
                  {
                    name: "する",
                    value: "true",
                  },
                  {
                    name: "しない",
                    value: "false",
                  },
                ]}
                id={`should_send_message_${mes.id}`}
                value={String(props.shouldSendMessage)}
                onChange={props.handleShouldSendMessageChange}
              />
            </FlexBox>
            <FlexBox alignItems="center">
              <Title as="h3" size={14} weight="bold">
                上限額
              </Title>
              <FlexBox>
                <Select
                  options={LENDABLE_LOANS.map(([value, name]) => ({
                    name,
                    value,
                    disabled: (() => {
                      return (
                        value * user.interest_add_on_when_examination >=
                        props.availablePayment * 1000
                      )
                    })(),
                  }))}
                  value={props.lendableLoan}
                  onChange={props.handleLendableLoanChange}
                />
              </FlexBox>
            </FlexBox>
            <FlexBox alignItems="center">
              <Title as="h3" size={14} weight="bold">
                決裁理由
              </Title>
              <FlexBox>
                <GrayRoundInput
                  value={props.reasonForDecision}
                  onChange={props.handleReasonForDecisionChange}
                />
              </FlexBox>
            </FlexBox>
            <FlexBox justifyContent="center">
              <BorderedButton
                data-data="通過"
                data-main_examination_score_id={mes.id}
                style={{ marginRight: 8 }}
                onClick={(e) => {
                  props.handleResultButtonClick(e)
                }}
              >
                決定
              </BorderedButton>
              <BorderedButton
                onClick={() => {
                  toggleDisplayActions({ all: false })
                }}
              >
                キャンセル
              </BorderedButton>
            </FlexBox>
          </Card>
        )}
      </SlideDown>
      <Border />
      {everyExaminationRelatedToProposal.isValidating ? (
        <div className={`main-examination-score__row`}>
          過去の審査結果を取得しています。しばらくお待ちください。
        </div>
      ) : null}
      {everyExaminationRelatedToProposal.data?.length ? (
        <>
          <FlexBox
            alignItems="center"
            justifyContent="space-between"
            className={`main-examination-score__row`}
          >
            <Title as="h3" size={14} weight="bold">
              過去の審査結果（{everyExaminationRelatedToProposal.data?.length}）
            </Title>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "80%",
              }}
            >
              {everyExaminationRelatedToProposal.data?.map(({ score = {}, ...me }) => (
                <div
                  key={me.id}
                  style={{
                    width: "100%",
                    marginBottom: 12,
                  }}
                >
                  <button
                    style={{
                      borderTop: "1px solid #ccc",
                      width: "100%",
                      padding: "8px 19px",
                      borderBottom: "1px solid #ccc",
                      backgroundColor: "#fafafa",
                      textAlign: "left",
                      maxWidth: 700,
                      fontWeight: "bold",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: 15,
                    }}
                    onClick={() => {
                      handleSetOpenedScore(me.id)
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>
                        {me.id}.{score.result}
                        <span
                          style={{
                            marginLeft: 8,
                          }}
                        >
                          {(() => {
                            if (!me.smoothExaminedAt) return ""
                            const now = dayjs()
                            const oneWeekAgo = dayjs(me.smoothExaminedAt)
                            const daysAgo = now.diff(oneWeekAgo, "day")
                            return `${daysAgo + 1}日前`
                          })()}
                        </span>
                      </span>
                    </span>
                    <span>{openedScore.includes(me.id) ? <BiUpArrow /> : <BiDownArrow />}</span>
                  </button>
                  <SlideDown>
                    {openedScore.includes(me.id) ? (
                      <div
                        style={{
                          width: 700,
                          padding: 2,
                          display: "flex",
                          flexWrap: "wrap",
                          borderBottom: "1px solid #ccc",
                        }}
                      >
                        <List
                          items={
                            me
                              ? [
                                  ["Cic Examination", me.cicExamination],
                                  ["Smooth Examined At", formatDate(me.smoothExaminedAt)],
                                  ["特例", `${me.exception}`],
                                  ["特例による調整金額", me.additionalAmountByExeption],
                                  ["Created At", formatDate(me.createdAt)],
                                  ["Updated At", formatDate(me.updatedAt)],
                                  [
                                    "MainExaminationScore",
                                    <a
                                      key={score?.id}
                                      href={
                                        V1_OPE_URL + "/admin/main_examination_scores/" + score?.id
                                      }
                                      style={{
                                        textDecoration: "underline",
                                        color: color.main,
                                      }}
                                      // className={styles.mainExaminationScoreItem}
                                      target="_blank"
                                    >
                                      ID:{` `}
                                      {score.id}
                                      <HiExternalLink />
                                    </a>,
                                  ],
                                  ["Result", score?.result],
                                  ["LendableLoan", score?.lendableLoan],
                                ]
                              : []
                          }
                        />
                      </div>
                    ) : null}
                  </SlideDown>
                </div>
              ))}
            </div>
          </FlexBox>
          <Border />
        </>
      ) : null}
      <FlexBox
        alignItems="center"
        justifyContent="flex-start"
        className={`main-examination-score__row`}
      >
        <Title as="h3" size={14} weight="bold">
          メモ
        </Title>
        <FlexBox flexDirection="column" className="memos">
          {user.memos.map((memo) => (
            <Memo
              key={memo.id}
              memo={memo}
              destroyMemo={props.handleDestroyMemo}
              openMemoForm={props.handleOpenMemoForm}
            />
          ))}
        </FlexBox>
        <FlexBox flexDirection="column" className="memo-form">
          <section className="">
            <GrayRoundInput
              type="text"
              onChange={props.handleChangeMemoForm}
              name={"title"}
              value={props.memoFormState.title}
              placeholder="メモのタイトルを入力して下さい"
            />
          </section>
          <section className="">
            <GrayRoundTextArea
              onChange={props.handleChangeMemoForm}
              name={"body"}
              value={props.memoFormState.body}
              rows={12}
              placeholder="メモの本文を入力して下さい"
            />
          </section>
          <div className={`${``}__actions`}>
            <BorderedButton>キャンセル</BorderedButton>
            <BorderedButton onClick={props.handleSubmitMemo}>保存</BorderedButton>
          </div>
        </FlexBox>
      </FlexBox>
    </Card>
  )
})

const StyledComponent = styled(Component)`
  .pass-actions {
    padding: 20px 0;
    width: 80%;
    margin: 10px auto;
    > .smooth-ui-FlexBox {
      width: 90%;
      margin: 14px auto;
      > .smooth-ui-FlexBox,
      > .radios {
        width: 300px;
      }

      .checkbox {
        height: 44px;
        margin-left: 8px;
      }

      > .smooth-ui-Title {
        width: 200px;
      }
    }
  }

  .icon-src {
    width: 50px;
    height: 50px;
    overflow: hidden;
    border-radius: 25px;
  }

  .adjust {
    .smooth-ui-GrayRoundInput {
      width: 120px;
    }
  }
`
type MemoFormState = {
  title: string
  body: string
}

type State = {
  storeId: number
  memoForm: {
    title: string
    body: string
  }
  lendableLoan: number | "all"
  // 千円単位
  claimPaymentAmountOnAnnual: number
  additionalAmountByException: number
  reasonForException: string
  reasonForDecision: string
  openedMemo: number | null
  exception: boolean
  shouldSendMessage: boolean
}

const Container: React.VFC<ContainerProps> = (props) => {
  const { deleteWithToken, patchWithToken, postWithToken } = useSmoothApi()
  const { showAlert, showConfirm, showSnackbar } = useNotification()

  const [state, setState] = useState<State>({
    storeId: props.proposal.store_id,
    memoForm: {
      title: "",
      body: "",
    },
    lendableLoan: null,
    claimPaymentAmountOnAnnual: null,
    additionalAmountByException: 0,
    reasonForException: "",
    reasonForDecision: "",
    openedMemo: null,
    exception: false,
    shouldSendMessage: true,
  })

  const handleDestroyMemo = async (memo: MemoType) => {
    try {
      await deleteWithToken(SMOOTH_SMOOTHER_API_URL + "/deserialized_memos/" + memo.id)
    } catch (e) {
      showAlert("失敗した。もう一回お願いします。")
      return
    }

    setState((current) => ({
      ...current,
      memoForm: { title: "", body: "" },
    }))
  }

  const storeSelectProps = {
    value: state.storeId,
    onChange: (value: number) => {
      handleStorePatch(value)
    },
  }

  const handleStorePatch = async (storeId: number) => {
    const payload = {
      store_id: storeId,
    }

    try {
      await patchWithToken(
        SMOOTH_SMOOTHER_API_URL + "/proposals/" + props.proposal.id + "/update_store",
        payload
      )
    } catch (e) {
      showAlert("失敗した。もう一回お願いします。")
      return
    }

    showAlert("更新完了")
    setState((current) => ({ ...current, storeId }))
  }

  const handleOpenMemoForm = (memo: MemoType) => {
    setState((current) => ({
      ...current,
      memoForm: memo,
      openedMemo: memo.id,
    }))
  }

  const handleChangeMemoForm: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (
    e
  ) => {
    setState((current) => ({
      ...current,
      memoForm: { ...current.memoForm, [e.target.name]: e.target.value },
    }))
  }

  const handleSubmitMemo = async () => {
    const payload = {
      memo: {
        memoable_id: props.currentSmoother.id,
        user_id: props.user.id,
        ...state.memoForm,
      },
    }

    try {
      state.openedMemo
        ? await patchWithToken(
            SMOOTH_SMOOTHER_API_URL + "/deserialized_memos/" + state.openedMemo,
            payload
          )
        : await postWithToken(SMOOTH_SMOOTHER_API_URL + "/deserialized_memos/", payload)
    } catch (e) {
      showAlert("失敗した。もう一回お願いします。")
      return
    }

    showAlert("メモ作成完了")
  }

  const handleCheckException: React.ChangeEventHandler<HTMLInputElement> = () => {
    setState((c) => ({
      ...c,
      exception: !c.exception,
      // 可決額リセット
      lendableLoan: !c.exception ? c.lendableLoan : null,
    }))
  }

  const handleAdditionalAmountChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (isNaN(Number(e.target.value))) {
      showAlert("半角英数字で入力してください！")
      return
    }
    setState((c) => ({
      ...c,
      additionalAmountByException: Number(e.target.value),
      lendableLoan: null,
    }))
  }

  const handleReasonForExceptionChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setState((c) => ({
      ...c,
      reasonForException: e.target.value,
    }))
  }

  const handleReasonForDecisionChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setState((c) => ({
      ...c,
      reasonForDecision: e.target.value,
    }))
  }

  const handleLendableLoanChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    setState((current) => ({
      ...current,
      lendableLoan: e.target.value === "all" ? "all" : Number(e.target.value),
    }))
  }

  const handleResultButtonClick: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    const data = e.currentTarget.dataset["data"]
    const mes_id = props.mes.id
    const messageFlg = state.shouldSendMessage
    let lendable_loan = state.lendableLoan

    if (data === "通過") {
      if (typeof state.claimPaymentAmountOnAnnual !== "number") {
        showAlert("年間請求予定額を入力してください！")
        return
      }

      if (!state.lendableLoan) {
        showAlert("金額を入力してください！")
        return
      }

      if (!state.storeId) {
        showAlert("店舗が割り当てられていません！")
        return
      }

      const age = CalcAge(props.user.latest_simplified_examination.birthday || props.user.birthday)

      if ([18, 19].includes(age)) {
        const result = window.confirm(age + "歳です。OK？")

        if (!result) return
      }
    }

    if (lendable_loan === "all") {
      lendable_loan = null
    } else {
      lendable_loan = Number(lendable_loan)
    }

    if (state.exception && !state.additionalAmountByException) {
      showAlert("調整額！")
      return
    }

    if (state.exception && !state.reasonForException) {
      showAlert("特例調査結果を入力してください！")
      return
    }

    const result = window.confirm(data + "にしますか？")

    const exception = Boolean(state.exception)

    if (data === "否認") {
      lendable_loan = null
    }

    if (result) {
      try {
        const payload = {
          main_examination_score: {
            id: mes_id,
            result: data,
            lendable_loan,
          },
          claim_payment_amount_on_annual: state.claimPaymentAmountOnAnnual * 1000,
          additional_amount_by_exception: state.additionalAmountByException * 1000,
          reason_for_exception: state.reasonForException,
          reason_for_decision: state.reasonForDecision,
          is_send_message_to_user: `${messageFlg}`,
          exception,
        }
        await patchWithToken(
          `${SMOOTH_SMOOTHER_API_URL}/main_examination_scores/` +
            props.mes.id +
            "/asynchronous_update",
          payload
        )
      } catch (e) {
        showAlert("失敗した。もう一回お願いします。")
        return
      }

      showSnackbar(data + "にしました", "success")
      props.mutateForCIC(mes_id)
    }
  }

  const handleShouldSendMessageChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setState((current) => ({
      ...current,
      shouldSendMessage: e.target.value === "true",
    }))
  }

  const availableSalary = useMemo(() => {
    const latestSimplifiedExamination = props.user?.latest_simplified_examination
    const household = latestSimplifiedExamination?.household
    const family_type = latestSimplifiedExamination?.family_type
    let cost = 1160
    switch (household) {
      case 1:
        cost = 1160
        if (family_type === "既婚") {
          cost = 1770
        }
        break
      case 2:
        cost = 1770
        break
      case 3:
        cost = 2090
        break
      default:
        cost = 2400
    }

    return Number(latestSimplifiedExamination?.income_yearly) * 10 - Number(cost)
  }, [props.user?.latest_simplified_examination])

  const availablePayment = useMemo(() => {
    if (typeof state.claimPaymentAmountOnAnnual === "number") {
      if (state.exception) {
        return (
          availableSalary + state.additionalAmountByException - state.claimPaymentAmountOnAnnual
        )
      }
      return availableSalary - state.claimPaymentAmountOnAnnual
    }
    return availableSalary
  }, [
    availableSalary,
    state.additionalAmountByException,
    state.claimPaymentAmountOnAnnual,
    state.exception,
  ])

  const handleSetClaimPaymentAmountOnAnnual: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setState((current) => ({
      ...current,
      lendableLoan: null,
      claimPaymentAmountOnAnnual: (!isNaN(Number(e.target.value))
        ? Number(e.target.value)
        : e.target.value) as number,
    }))
  }

  return (
    <StyledComponent
      {...props}
      id={props.mes.id}
      handleDestroyMemo={handleDestroyMemo}
      storeSelectProps={storeSelectProps}
      handleOpenMemoForm={handleOpenMemoForm}
      memoFormState={state.memoForm}
      handleChangeMemoForm={handleChangeMemoForm}
      handleSubmitMemo={handleSubmitMemo}
      handleLendableLoanChange={handleLendableLoanChange}
      shouldSendMessage={state.shouldSendMessage}
      handleShouldSendMessageChange={handleShouldSendMessageChange}
      availablePayment={availablePayment}
      lendableLoan={state.lendableLoan}
      claimPaymentAmountOnAnnual={state.claimPaymentAmountOnAnnual}
      handleSetClaimPaymentAmountOnAnnual={handleSetClaimPaymentAmountOnAnnual}
      handleResultButtonClick={handleResultButtonClick}
      exception={state.exception}
      handleCheckException={handleCheckException}
      additionalAmountByException={state.additionalAmountByException}
      handleAdditionalAmountChange={handleAdditionalAmountChange}
      reasonForException={state.reasonForException}
      handleReasonForExceptionChange={handleReasonForExceptionChange}
      reasonForDecision={state.reasonForDecision}
      handleReasonForDecisionChange={handleReasonForDecisionChange}
    />
  )
}

export const MainExaminationScore = Container
